// SPDX-License-Identifier: GPL-3.0-or-later
// The visual feedback of the game page: the effects layer in the scene, wired to the one feedback
// bus and the one settings store of the page.
//
// Play is calm. The page shows no floating score text and no callout banner on the playfield.
// The HUD score, the live region and the sound say what happens.
//
// - `frame` runs once for each physics frame, BEFORE the renderer draws it. It moves the effects.
// - The reduced motion choice of the page (the check box in Settings) lowers the effects to
//   "reduced" even when the effects level is "full".

import type { PinballRenderer } from "../src/render/index.ts";
import { createVfx, type Vfx } from "../src/render/vfx/index.ts";
import type { RenderFrame } from "../src/shared/contracts.ts";
import type { FeedbackBus, FlashLimiter, SettingsStore } from "../src/shared/feedback/index.ts";

/** The size of the playfield in CSS pixels. */
export interface ViewBox {
  readonly width: number;
  readonly height: number;
}

/** What the layer takes from the page. */
export interface VisualLayerDeps {
  readonly view: PinballRenderer;
  readonly bus: Pick<FeedbackBus, "subscribe">;
  readonly settings: Pick<SettingsStore, "get">;
  readonly limiter: Pick<FlashLimiter, "tryFlash">;
  /** The clock in ms. The page passes the same clock to `frame`. */
  readonly now: () => number;
}

/** The visual layer. */
export interface VisualLayer {
  /** Move the effects on. Call it before the renderer draws the frame. */
  frame(nowMs: number, dtMs: number, frame: RenderFrame): void;
  /** The reduced motion choice of the page. */
  setReducedMotion(on: boolean): void;
  /** The number of particles that still live. A reader (a test) uses it to see the effects move. */
  readonly liveParticles: number;
  /** Remove the effects. */
  dispose(): void;
}

/** Make the visual layer. */
export function createVisualLayer(deps: VisualLayerDeps): VisualLayer {
  const vfx: Vfx = createVfx({
    renderer: deps.view,
    bus: deps.bus,
    settings: deps.settings,
    limiter: deps.limiter,
    now: deps.now,
  });
  return {
    frame: (nowMs, dtMs, frame) => vfx.update(nowMs, dtMs, frame),
    setReducedMotion: (on) => vfx.setReducedMotion(on),
    get liveParticles() {
      return vfx.liveParticles;
    },
    dispose: () => vfx.dispose(),
  };
}
